import { useEffect, useRef, useState } from "react";
import { describeImage } from "@/content/image-descriptions";
import { resolveDescribedImage } from "@/lib/describedImages";
import {
  BLOG_IMAGE_FALLBACK,
  BLOG_IMAGE_FALLBACK_PLACEHOLDER,
} from "@/content/blog/generated-image-manifest";

type BlogCardImageProps = {
  src: string;
  placeholderSrc?: string;
  alt: string;
  className?: string;
};

export function BlogCardImage({ src, placeholderSrc, alt, className }: BlogCardImageProps) {
  const [currentSrc, setCurrentSrc] = useState(src);
  const [currentPlaceholder, setCurrentPlaceholder] = useState(
    placeholderSrc ?? BLOG_IMAGE_FALLBACK_PLACEHOLDER,
  );
  const [isLoaded, setIsLoaded] = useState(false);
  const [showPlaceholder, setShowPlaceholder] = useState(Boolean(placeholderSrc));
  const currentSrcRef = useRef(src);
  const resolvedRef = useRef(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    setCurrentSrc(src);
    setCurrentPlaceholder(placeholderSrc ?? BLOG_IMAGE_FALLBACK_PLACEHOLDER);
    setIsLoaded(false);
    setShowPlaceholder(Boolean(placeholderSrc));
    currentSrcRef.current = src;
    resolvedRef.current = false;

    const img = imgRef.current;
    if (img && img.complete && img.naturalHeight > 0) {
      setIsLoaded(true);
      setShowPlaceholder(false);
      resolvedRef.current = true;
    }

    return undefined;
  }, [src, placeholderSrc]);

  const handleLoad = () => {
    setIsLoaded(true);
    setShowPlaceholder(false);
    resolvedRef.current = true;
  };

  const handleError = () => {
    if (currentSrcRef.current !== BLOG_IMAGE_FALLBACK) {
      setCurrentSrc(BLOG_IMAGE_FALLBACK);
      setCurrentPlaceholder(BLOG_IMAGE_FALLBACK_PLACEHOLDER);
      currentSrcRef.current = BLOG_IMAGE_FALLBACK;
      setIsLoaded(false);
      setShowPlaceholder(Boolean(placeholderSrc));
    } else {
      setIsLoaded(true);
      setShowPlaceholder(false);
      resolvedRef.current = true;
    }
  };

  return (
    <div className={`demo-image-frame demo-image-frame-card${className ? ` ${className}` : ""}`}>
      {showPlaceholder && (
        <img
          src={resolveDescribedImage(currentPlaceholder)}
          alt=""
          aria-hidden="true"
          className="demo-image-blur-placeholder"
          loading="lazy"
          decoding="async"
        />
      )}
      {!isLoaded && !showPlaceholder && <div className="demo-image-skeleton" aria-hidden="true" />}
      <img
        ref={imgRef}
        src={resolveDescribedImage(currentSrc)}
        alt={describeImage(resolveDescribedImage(currentSrc), alt)}
        title={describeImage(resolveDescribedImage(currentSrc), alt)}
        data-name={alt}
        className={`demo-image-media img-fluid ${isLoaded ? "is-loaded" : ""}`}
        loading="lazy"
        decoding="async"
        onLoad={handleLoad}
        onError={handleError}
      />
    </div>
  );
}
